<template>
	<div class="application-page">
		<b-container>
			<b-row>
				<b-col md="3">
					<Navigation page="job-edit"/>
				</b-col>
				<b-col md="9">
					<div class="sticky-title" v-sticky>
						<h1>Mein Jobwunsch</h1>
					</div>
					<Loading v-if="loading && !initiallyLoadedJob"/>
					<div v-else class="main-content profile-view">
						<div :class="['tag-sections', { 'loading-blockade': loading && initiallyLoadedJob }]">
							<section
								v-for="group in groupedItems"
								:key="group.category.id"
							>
								<Title
									:title="group.category.label"
									parent
									:editable="false"
									:completion-type="getCategoryCompletionType(group.category)"
								/>
								<div class="sub-sections">
									<div
										v-for="section in group.sections"
										:key="section.id"
										class="sub-section"
									>
										<Title
											:title="getSectionLabel(section)"
											:completion-type="getSectionCompletedType(section)"
											:tooltip="getSectionTooltip(section)"
											@edit="edit(section)"
										/>
										<SelectedItems
											v-if="section.id === 'function'"
											:characteristic-items="meta.function"
											:characteristic-type="meta.functionType"
											:selected="filterLastLevel(job.functions)"
											:label-type="labelTypeApplication"
											flat
											none-info="Alle Tätigkeiten möglich"
										/>
										<SelectedItems
											v-if="section.id === 'employment-mode'"
											:characteristic-items="meta.employmentMode"
											:characteristic-type="meta.employmentModeType"
											:selected="filterLastLevel(job.employmentMode)"
											:label-type="labelTypeApplication"
											flat
										/>
										<SelectedItems
											v-if="section.id === 'working-time'"
											:characteristic-items="meta.workingTime"
											:characteristic-type="meta.workingTimeType"
											:selected="filterLastLevel(job.workingTime.workingTime)"
											:label-type="labelTypeApplication"
											none-info="Keine"
											flat
										/>
										<SelectedItems
											v-if="section.id === 'geo'"
											:characteristic-items="geoItems"
											:characteristic-type="meta.geoType"
											:selected="job.geo"
											:label-type="labelTypeApplication"
											:no-children-appendix="{
												1: 'Alle Regionen/Bezirke möglich'
											}"
										/>
										<SelectedItems
											v-if="section.id === 'career-level'"
											:characteristic-items="meta.careerLevel"
											:characteristic-type="meta.careerLevelType"
											:selected="filterLastLevel(job.careerLevels)"
											:label-type="labelTypeApplication"
											flat
										/>
										<SelectedItems
											v-if="section.id === 'industrial-sector'"
											:characteristic-items="meta.industrialSector"
											:characteristic-type="meta.industrialSectorType"
											:selected="filterLastLevel(job.industrialSectors)"
											:label-type="labelTypeApplication"
											flat
											none-info="Alle Branchen möglich"
										/>
										<SelectedItems
											v-if="section.id === 'organisational-area'"
											:characteristic-items="meta.organisationalArea"
											:characteristic-type="meta.organisationalAreaType"
											:selected="filterLastLevel(job.organisationalArea)"
											:label-type="labelTypeApplication"
											flat
											none-info="Alle Organisationsbereiche möglich"
										/>
										<SelectedItems
											v-if="section.id === 'function-marketing-role'"
											:characteristic-items="meta.functionMarketingRole"
											:characteristic-type="meta.functionMarketingRoleType"
											:selected="filterLastLevel(job.functionMarketingRole)"
											:label-type="labelTypeApplication"
											flat
										/>
										<SelectedItems
											v-if="section.id === 'function-legal-role'"
											:characteristic-items="meta.functionLegalRole"
											:characteristic-type="meta.functionLegalRoleType"
											:selected="filterLastLevel(job.functionLegalRole)"
											:label-type="labelTypeApplication"
											flat
										/>
										<SelectedItems
											v-if="section.id === 'function-legal-field'"
											:characteristic-items="meta.functionLegalField"
											:characteristic-type="meta.functionLegalFieldType"
											:selected="filterLastLevel(job.functionLegalField)"
											:label-type="labelTypeApplication"
											flat
										/>
										<SelectedItems
											v-if="section.id === 'function-tax-role'"
											:characteristic-items="meta.functionTaxRole"
											:characteristic-type="meta.functionTaxRoleType"
											:selected="filterLastLevel(job.functionTaxRole)"
											:label-type="labelTypeApplication"
											flat
										/>
										<SelectedItems
											v-if="section.id === 'function-tax-field'"
											:characteristic-items="meta.functionTaxField"
											:characteristic-type="meta.functionTaxFieldType"
											:selected="filterLastLevel(job.functionTaxField)"
											:label-type="labelTypeApplication"
											flat
										/>
										<SelectedItems
											v-if="section.id === 'search-status' && job.searchStatus"
											:characteristic-items="meta.searchStatus"
											:characteristic-type="meta.searchStatusType"
											:selected="[job.searchStatus.searchStatus]"
											:label-type="labelTypeApplication"
											flat
											class="inline"
										/>
										<Parking
											v-if="section.id === 'parking' && job.parking"
											:data="job.parking"
										/>
										<HomeOffice
											v-if="section.id === 'home-office' && job.homeOffice"
											:data="job.homeOffice"
										/>
										<StartDate
											v-if="section.id === 'start-date' && job.startDate !== undefined"
											:data="job.startDate"
										/>
										<SelectedItems
											v-if="section.id === 'public-transport' && job.publicTransport"
											:characteristic-items="meta.publicTransport"
											:characteristic-type="meta.publicTransportType"
											:selected="filterLastLevel(job.publicTransport.publicTransport)"
											:label-type="labelTypeApplication"
											flat
											none-info="Egal"
											class="inline"
										/>
										<CompanyCar
											v-if="section.id === 'company-car' && job.companyCar !== undefined"
											:data="job.companyCar"
										/>
										<PrivateVehicleCompanyUsage
											v-if="section.id === 'private-vehicle-company-usage' && job.privateVehicleCompanyUsage !== undefined"
											:data="job.privateVehicleCompanyUsage"
										/>
										<IrrelevantOrDesireOrNone
											v-if="section.id === 'customer-contact' && job.customerContact"
											:characteristic-items="meta.businessModel"
											:characteristic-type="meta.businessModelType"
											:data="job.customerContact"
										/>
										<SelectedItems
											v-if="section.id === 'company-size' && job.companySize"
											:characteristic-items="meta.companySize"
											:characteristic-type="meta.companySizeType"
											:selected="filterLastLevel(job.companySize)"
											:label-type="labelTypeApplication"
											flat
											none-info="Egal - alle sind möglich"
											class="inline"
										/>
										<SelectedItems
											v-if="section.id === 'company-type' && job.companyType"
											:characteristic-items="meta.companyType"
											:characteristic-type="meta.companyTypeType"
											:selected="filterLastLevel(job.companyType)"
											:label-type="labelTypeApplication"
											flat
											none-info="Egal - alle sind möglich"
											class="inline"
										/>
										<Overtime
											v-if="section.id === 'overtime' && job.overtime"
											:data="job.overtime"
										/>
										<IrrelevantOrDesireOrNone
											v-if="section.id === 'management' && job.management"
											:characteristic-items="meta.managementKind"
											:characteristic-type="meta.managementKindType"
											:data="job.management"
										/>
										<Relocation
											v-if="section.id === 'relocation' && job.relocation"
											:data="job.relocation"
										/>
										<TravelWillingness
											v-if="section.id === 'travel-willingness' && job.travelWillingness"
											:data="job.travelWillingness"
										/>
										<Salary
											v-if="section.id === 'salary' && job.salary !== undefined"
											:job="job"
											:data="job.salary"
										/>
										<HourlyRate
											v-if="section.id === 'hourly-rate' && job.hourlyRate !== undefined"
											:job="job"
											:data="job.hourlyRate"
										/>
										<EmploymentDuration
											v-if="section.id === 'employment-duration' && job.employmentDuration"
											:data="job.employmentDuration"
										/>
										<WorkingHours
											v-if="section.id === 'working-hours' && job.workingHours"
											:data="job.workingHours"
										/>
									</div>
								</div>
							</section>
						</div>
					</div>
				</b-col>
			</b-row>
		</b-container>
	</div>
</template>

<script>
import Navigation from "@/vue/application/profile/Navigation";
import GetList from "@/api/application/job/GetList";
import {mapActions, mapState} from "vuex";
import Loading from "@/vue/common/Loading";
import _ from "lodash";
import Title, {COMPLETION_TYPE__COMPLETED, COMPLETION_TYPE__INCOMPLETE} from "@/vue/common/profile-view/Title.vue";
import SelectedItems from "@/vue/characteristic/SelectedItems";
import {characteristicMultiple} from "@/helper/application/Populate";
import {APPLICATION} from "@/helper/characteristic/Label";
import Parking from "./job-edit/Parking";
import HomeOffice from "./job-edit/HomeOffice";
import StartDate from "./job-edit/StartDate";
import IrrelevantOrDesireOrNone from "./job-edit/IrrelevantOrDesireOrNone";
import PrivateVehicleCompanyUsage from "./job-edit/PrivateVehicleCompanyUsage";
import CompanyCar from "./job-edit/CompanyCar";
import Overtime from "./job-edit/Overtime";
import Relocation from "./job-edit/Relocation";
import TravelWillingness from "./job-edit/TravelWillingness";
import Salary from "./job-edit/Salary";
import HourlyRate from "./job-edit/HourlyRate";
import EmploymentDuration from "./job-edit/EmploymentDuration";
import WorkingHours from "./job-edit/WorkingHours";
import * as Section from "@/helper/application/Section"
import {populate} from "@/helper/job/Populate";
import {filterLastLevel} from "@/helper/characteristic/Characteristic";
import * as MaxDepth from "@/helper/characteristic/MaxDepth";
import * as Event from "@/helper/tracking/Event";
import EventData from "@/helper/tracking/EventData";

const CONFIGS = {
	[Section.EMPLOYMENT_MODE]: {
		dataProvider: (job) => job.employmentMode
	},
	[Section.WORKING_TIME]: {
		dataProvider: (job) => job.workingTime
	},
	[Section.EMPLOYMENT_DURATION]: {
		dataProvider: (job) => job.employmentDuration
	},
	[Section.GEO]: {
		fanTitle: 'Welche Arbeitsorte sind möglich?',
		dataProvider: (job) => job.geo
	},
	[Section.HOME_OFFICE]: {
		fanTitle: 'Ist Homeoffice möglich/gewünscht?',
		labelAppendix: 'gewünscht/möglich',
		dataProvider: (job) => job.homeOffice
	},
	[Section.CAREER_LEVEL]: {
		fanTitle: 'Welche Berufsebenen möchtest du?',
		dataProvider: (job) => job.careerLevels
	},
	[Section.INDUSTRIAL_SECTOR]: {
		fanTitle: `Arbeitgeber: Was soll dein zukünftiger Arbeitgeber machen? Soll er ein bestimmtes
		Tätigkeitsfeld / Branche haben?`,
		labelAppendix: '(Branchen, Leistungen, Produkte)',
		dataProvider: (job) => job.industrialSectors
	},
	[Section.ORGANISATIONAL_AREA]: {
		fanTitle: `In welchen Organisationsbereichen soll deine Tätigkeiten sein?`,
		dataProvider: (job) => job.organisationalArea
	},
	[Section.SALARY]: {
		fanTitle: 'Wie viel möchtest du verdienen?',
		dataProvider: (job) => job.salary
	},
	[Section.HOURLY_RATE]: {
		fanTitle: 'Welchen Mindest-Stundensatz hättest du gerne?',
		dataProvider: (job) => job.hourlyRate
	},
	[Section.WORKING_HOURS]: {
		labelAppendix: 'gewünscht/möglich',
		dataProvider: (job) => job.workingHours
	},
	[Section.WORKING_TIME]: {
		labelAppendix: 'möglich',
		fanTitle: 'Wären für dich spezielle Arbeitszeiten in Ordnung?',
		dataProvider: (job) => job.workingTime
	},
	[Section.EMPLOYMENT_DURATION]: {
		labelAppendix: 'gewünscht/möglich',
		dataProvider: (job) => job.employmentDuration
	},
	[Section.START_DATE]: {
		fanTitle: 'Ab wann möchtest du eine Tätigkeit?',
		dataProvider: (job) => job.startDate
	},
	[Section.SEARCH_STATUS]: {
		fanTitle: 'Wie aktiv suchst du eine neue Tätigkeit?',
		dataProvider: (job) => job.searchStatus
	},
	[Section.PARKING]: {
		fanTitle: 'Benötigst du einen Parkplatz beim Arbeitsort?',
		labelAppendix: 'gewünscht/erforderlich',
		dataProvider: (job) => job.parking
	},
	[Section.PUBLIC_TRANSPORT]: {
		fanTitle: 'Benötigst du eine Anbindung an öffentliche Verkehrsmittel?',
		labelAppendx: 'gewünscht/erforderlich',
		dataProvider: (job) => job.publicTransport
	},
	[Section.PRIVATE_VEHICLE_COMPANY_USAGE]: {
		fanTitle: `Würdest du dein Privatfahrzeug auch für berufliche Fahrten verwenden?<br/>
			ArbeitgeberInnen leisten dafür in der Regel Kostenersatz.`,
		labelAppendix: 'möglich',
		dataProvider: (job) => job.privateVehicleCompanyUsage
	},
	[Section.CUSTOMER_CONTACT]: {
		label: 'Magst du in deinem Job Kontakt mit externen Personen?',
		fanTitle: 'Möchtest du Kontakt mit externen Personen, wie Kunden, Klienten, Patienten, Bürger, Interessenten?',
		titleTooltip: 'Externe Personen sind zB Klienten, Kunden, Patienten, Bürger, Interessenten und andere',
		dataProvider: (job) => job.customerContact
	},
	[Section.TRAVEL_WILLINGNESS]: {
		fanTitle: 'Sind berufliche Reisen möglich?',
		labelAppendix: 'gegeben',
		dataProvider: (job) => job.travelWillingness
	},
	[Section.COMPANY_SIZE]: {
		fanTitle: 'Welche Organisationsgrößen sind möglich',
		labelAppendix: 'gewünscht',
		dataProvider: (job) => job.companySize
	},
	[Section.COMPANY_TYPE]: {
		fanTitle: 'Welche Organisationen sind möglich als Arbeitgeber?',
		labelAppendix: 'gewünscht',
		dataProvider: (job) => job.companyType
	},
	[Section.MANAGEMENT]: {
		fanTitle: 'Möchtest du andere MitarbeiterInnen führen?',
		labelAppendix: 'gewünscht/möglich',
		dataProvider: (job) => job.management
	},
	[Section.OVERTIME]: {
		fanTitle: 'Würdest du auch Überstunden machen?',
		labelAppendix: 'möglich',
		dataProvider: (job) => job.overtime
	},
	[Section.RELOCATION]: {
		fanTitle: 'Würdest du für einen passenden Job übersiedeln?',
		labelAppendix: 'gegeben',
		dataProvider: (job) => job.relocation
	},
	[Section.COMPANY_CAR]: {
		fanTitle: 'Benötigst oder erwartest du ein Firmenauto?',
		labelAppendix: 'erforderlich/erwartet',
		dataProvider: (job) => job.companyCar
	},
	[Section.FUNCTION]: {
		fanTitle: 'Welche Tätigkeiten möchtest du / sind möglich?',
		dataProvider: (job) => job.functions
	},
	[Section.FUNCTION_MARKETING_ROLE]: {
		fanTitle: 'Welche Marketing-Tätigkeiten möchtest du / sind möglich?',
		dataProvider: (job) => job.functionMarketingRole
	},
	[Section.FUNCTION_LEGAL_FIELD]: {
		fanTitle: 'Welche Rechtsgebiete möchtest du / sind möglich?',
		dataProvider: (job) => job.functionLegalField
	},
	[Section.FUNCTION_LEGAL_ROLE]: {
		fanTitle: 'Welche Rechtstätigkeiten möchtest du / sind möglich?',
		dataProvider: (job) => job.functionLegalRole
	},
	[Section.FUNCTION_TAX_FIELD]: {
		fanTitle: 'Welche Steuergebiete möchtest du / sind möglich?',
		dataProvider: (job) => job.functionTaxField
	},
	[Section.FUNCTION_TAX_ROLE]: {
		fanTitle: 'Welche Steuertätigkeiten möchtest du / sind möglich?',
		dataProvider: (job) => job.functionTaxRole
	},
};

export default {

	components: {
		IrrelevantOrDesireOrNone,
		Parking,
		HomeOffice,
		SelectedItems,
		Title,
		Loading,
		Navigation,
		CompanyCar,
		StartDate,
		PrivateVehicleCompanyUsage,
		Overtime,
		Relocation,
		TravelWillingness,
		Salary,
		HourlyRate,
		EmploymentDuration,
		WorkingHours,
	},

	data ()
	{
		return {
			job: undefined,
			sectionCompletions: [],
			loadingJob: true,
			initiallyLoadedJob: false,
			saving: false,
		};
	},

	computed: {
		...mapState('meta', ['meta']),
		...mapState('auth', [ 'internal' ]),
		geoItems ()
		{
			return MaxDepth.filter(this.meta.geo, 2);
		},
		labelTypeApplication ()
		{
			return APPLICATION;
		},
		loading ()
		{
			return this.loadingJob;
		},
		groupedItems ()
		{
			let grouped = {};

			_.forEach(this.job.editableSections, section =>
			{
				const category = section.category;
				const categoryId = category.id;

				if (!grouped[categoryId])
				{
					grouped[categoryId] = {
						category,
						sections: []
					};
				}

				grouped[categoryId].sections.push(section);
			})

			return _.sortBy(_.values(grouped), 'category.sort');
		},
	},

	methods: {
		...mapActions('job/edit', {
			'openJobEditFan': 'show'
		}),
		...mapActions('job/bot', [
			'showQuestionnaire'
		]),
		getSectionTooltip (section)
		{
			return CONFIGS[section.id]?.titleTooltip;
		},
		getSectionLabel (section)
		{
			const config = CONFIGS[section.id];

			if (config.label)
			{
				return config.label;
			}

			let label = section.label;

			if (config?.labelAppendix)
			{
				label += ' ' + config.labelAppendix;
			}

			label += this.isSectionCompleted(section)
				? ':'
				: '?';

			return label;
		},
		filterLastLevel (items)
		{
			if (!items)
			{
				return items;
			}

			return filterLastLevel(items);
		},
		edit (section)
		{
			const config = CONFIGS[section.id];

			if (!config)
			{
				return;
			}

			Event.push(
				new EventData()
					.setEvent(Event.APPLICANT_JOB)
					.setAction('Tag-Gruppe bearbeiten')
					.setName(section.label)
			);

			const data = config.dataProvider(this.job, this.meta);

			this.openJobEditFan({
				title: config.fanTitle,
				job: this.job,
				initialData: _.cloneDeep(data),
				sectionToEdit: section,
				onSave: async ({confirmed}) =>
				{
					if (confirmed)
					{
						this.loadJob();
					}
				}
			});
		},
		characteristicMultiple (list, metaItems, metaType)
		{
			return characteristicMultiple(list, this.meta, metaItems, metaType, true);
		},
		getCategoryCompletionType (category)
		{
			const sections = _.find(this.groupedItems, item => item.category.id === category.id).sections;

			const completedSections = _.filter(sections, section => this.isSectionCompleted(section));

			return completedSections.length === sections.length
				? COMPLETION_TYPE__COMPLETED
				: COMPLETION_TYPE__INCOMPLETE;
		},
		isSectionCompleted (section)
		{
			return _.includes(this.sectionCompletions, section.id);
		},
		getSectionCompletedType (section)
		{
			return this.isSectionCompleted(section)
				? COMPLETION_TYPE__COMPLETED
				: COMPLETION_TYPE__INCOMPLETE;
		},
		async loadJob ()
		{
			this.loadingJob = true;

			const response = await new GetList()
				.call();

			this.job = populate({
				job: _.first(response.data),
				meta: this.meta,
			});

			this.sectionCompletions = this.job.sectionCompletionsNotSkipped;

			if (!this.job.questionnaire.finished && !this.internal)
			{
				this.showQuestionnaire({
					job: this.job,
					postFinish: true,
					onClose: () =>
					{
						this.loadJob();
					},
				});

				return;
			}

			this.loadingJob = false;
			this.initiallyLoadedJob = true;
		},
	},

	watch: {
		internal ()
		{
			// load job not on mounted, but here, because we have to know if we are internal or not before loading
			this.loadJob();
		}
	}
}
</script>
